import { useEffect, useState } from 'react';
import { Card, Field } from '../../../components/ui';
import { LANG_NAMES, langDir, langFont } from '../../../lib/format';
import type { BannerText } from '../../../lib/api';
import { PACK_DEFAULTS } from './packs';
import { TEXT_LIMITS, type CardProps } from './defaults';

const FIELDS: Array<[keyof BannerText, string, boolean]> = [
  ['title', 'Title', false],
  ['body', 'Body', true],
  ['accept', 'Accept all', false],
  ['reject', 'Reject all', false],
  ['customise', 'Customise', false],
  ['noticeLink', 'Notice link', false],
  ['reopen', 'Reopen label', false],
];

export interface Language { code: string; endonym?: string }

/**
 * One tab per language the notice is published in. A blank field means the
 * banner script's own wording for that language, shown as the placeholder.
 */
export default function TextCard({ a, update, readOnly, languages, lang, onLang }: CardProps & {
  languages: Language[];
  lang: string;
  onLang: (code: string) => void;
}) {
  const [tab, setTab] = useState(lang);
  useEffect(() => { setTab(lang); }, [lang]);
  const code = languages.some((l) => l.code === tab) ? tab : (languages[0]?.code ?? 'en');
  const text = a.text[code] ?? {};
  const pack = PACK_DEFAULTS[code];
  const dir = langDir(code);
  const font = langFont(code);

  const setText = (key: keyof BannerText, value: string) => update((x) => {
    const next = { ...(x.text[code] ?? {}) };
    if (value) next[key] = value; else delete next[key];
    const text = { ...x.text };
    if (Object.keys(next).length) text[code] = next; else delete text[code];
    return { ...x, text };
  });

  const label = (l: Language) => l.endonym ?? LANG_NAMES[l.code] ?? l.code;

  return (
    <Card title="Text">
      {languages.length > 1 && (
        <div className="tabs" role="tablist" aria-label="Language">
          {languages.map((l) => (
            <button
              key={l.code} role="tab" aria-selected={l.code === code}
              className={l.code === code ? 'active' : ''}
              onClick={() => { setTab(l.code); onLang(l.code); }}
            >
              {label(l)}{a.text[l.code] && Object.keys(a.text[l.code]!).length ? ' ·' : ''}
            </button>
          ))}
        </div>
      )}
      {FIELDS.map(([key, name, long]) => {
        const placeholder = pack?.[key] ?? 'Standard wording';
        const value = text[key] ?? '';
        const limit = TEXT_LIMITS[key];
        return (
          <Field key={key} label={name} help={value ? `${value.length} of ${limit}` : undefined}>
            {long ? (
              <textarea
                value={value} placeholder={placeholder} maxLength={limit} rows={3} dir={dir} readOnly={readOnly}
                style={font ? { fontFamily: font } : undefined}
                onChange={(e) => setText(key, e.target.value)}
              />
            ) : (
              <input
                type="text" value={value} placeholder={placeholder} maxLength={limit} dir={dir} readOnly={readOnly}
                style={font ? { fontFamily: font } : undefined}
                onChange={(e) => setText(key, e.target.value)}
              />
            )}
          </Field>
        );
      })}
    </Card>
  );
}
